import { route, SandboxError, type Route } from '../adapter';
import { matches, newId, now, paginate, sortBy, type SandboxStore } from '../store';
import type { DonationItem, DonationItemInput, EventCsr, EventCsrPlan, EventDetail, EventRecap, EventRecording, EventSummary, LocalContact, LocalContactWrite } from '@/lib/events';
import { PORTAL_EVENT_ID, type PortalEvent } from '../seed/portal';
import type { CharityPlan } from '@/lib/charities';

/** The event list, the sample event and the panels on its Overview. */

/** One of the client portal's events (the Northwind Bike Build), when the id is one. */
function portalEvent(store: SandboxStore, id: string): PortalEvent | undefined {
  return store.portal.events.find((event) => event.detail._id === id);
}

function eventOr404(store: SandboxStore, id: string): EventDetail {
  if (id === store.eventId) return store.event;
  const portal = portalEvent(store, id);
  if (portal) return portal.detail;
  const row = store.eventList.find((event) => event._id === id);
  if (!row) throw new SandboxError(404, 'Event not found.');
  return row as EventDetail;
}

/** The charity side of an event: the portal event's own, or the sample event's. */
function csrOf(store: SandboxStore, id: string): EventCsr {
  return portalEvent(store, id)?.csr ?? store.csr;
}

function setCsr(store: SandboxStore, id: string, csr: EventCsr): void {
  const portal = portalEvent(store, id);
  if (portal) portal.csr = csr;
  else store.csr = csr;
}

/** Stores a new donation list on the event's plan, with the totals it adds up to, as the API does. */
function withDonationItems(store: SandboxStore, id: string, items: readonly DonationItem[]): EventCsrPlan {
  const donationCounts: Record<string, number> = {};
  for (const item of items) {
    if (item.kind !== undefined) donationCounts[item.kind] = (donationCounts[item.kind] ?? 0) + item.quantity;
  }
  const csr = csrOf(store, id);
  const plan: EventCsrPlan = { ...(csr.plan ?? {}), donationItems: items, donationCounts, updatedAt: now() };
  setCsr(store, id, { ...csr, plan });
  return plan;
}

/** Every recap in the sandbox, with the event it belongs to. */
function allRecaps(store: SandboxStore): EventRecap[] {
  return store.portal.events.flatMap((event) => event.recaps);
}

function donationItemOf(id: string, input: DonationItemInput): DonationItem {
  return { id, description: input.description, quantity: input.quantity, ...(input.kind ? { kind: input.kind } : {}) };
}

function stateOf(row: EventSummary): 'upcoming' | 'past' {
  const date = row.eventDate ? Date.parse(row.eventDate) : Number.NaN;
  return Number.isNaN(date) || date >= Date.now() - 24 * 60 * 60 * 1000 ? 'upcoming' : 'past';
}

function pick(row: EventSummary, field: string): string | number | undefined {
  switch (field) {
    case 'eventDate':
    case 'date':
      return row.eventDate ?? undefined;
    case 'participants':
      return row.participants ?? undefined;
    case 'location':
    case 'city':
      return row.startVenue?.city ?? undefined;
    case 'stage':
    case 'stageName':
      return row.stageName ?? undefined;
    default:
      return row.accountName ?? undefined;
  }
}

/** Fathom, Loom and Drive links play in the app; anything else is kept as a link. */
function recordingFor(body: { url: string; title: string; recordedAt?: string; viewableBy: readonly ('internal' | 'contractor')[] }): EventRecording {
  const url = body.url;
  const provider = /fathom\.video\//.test(url) ? 'fathom' : /loom\.com\//.test(url) ? 'loom' : /drive\.google\.com\/file\/d\//.test(url) ? 'drive' : 'link';
  const driveId = /drive\.google\.com\/file\/d\/([^/]+)/.exec(url)?.[1];
  const embedUrl = provider === 'loom' ? url.replace('/share/', '/embed/') : provider === 'fathom' ? url.replace('/share/', '/embed/') : driveId ? `https://drive.google.com/file/d/${driveId}/preview` : null;
  return { _id: newId(), provider: provider as EventRecording['provider'], url, embedUrl, title: body.title, ...(body.recordedAt ? { recordedAt: body.recordedAt } : {}), viewableBy: body.viewableBy, createdAt: now() };
}

export const eventRoutes: readonly Route[] = [
  route('GET', '/events', ({ store, query }) => {
    const state = query.get('state');
    const family = query.get('family');
    let rows = store.eventList.filter((row) => (state && state !== 'all' ? stateOf(row) === state : true));
    if (family) rows = rows.filter((row) => (row.contractNumber ?? '').startsWith(family));
    rows = rows.filter((row) => matches(query.get('search'), row.accountName, row.programName, row.programDisplayName, row.contractNumber, row.leadFacilitatorName, row.location));
    rows = sortBy(rows, query.get('sortField') ?? 'eventDate', query.get('sortDirection') ?? 'asc', pick);
    return paginate(rows, query);
  }),
  route('GET', '/events/:id', ({ store, params }) => ({ data: eventOr404(store, params['id'] ?? '') })),
  route('GET', '/events/:id/csr', ({ store, params }) => ({ data: csrOf(store, params['id'] ?? '') })),
  route('PATCH', '/events/:id/csr', ({ store, params, body }) => {
    const id = params['id'] ?? '';
    const csr = csrOf(store, id);
    const plan = { donationItems: [], ...(csr.plan ?? {}), ...(body as object), updatedAt: now() };
    setCsr(store, id, { ...csr, plan });
    return { data: plan };
  }),
  route('POST', '/events/:id/csr/donation-items', ({ store, params, body }) => {
    const id = params['id'] ?? '';
    const items = csrOf(store, id).plan?.donationItems ?? [];
    return { data: withDonationItems(store, id, [...items, donationItemOf(newId(), body as DonationItemInput)]) };
  }),
  route('PATCH', '/events/:id/csr/donation-items/:itemId', ({ store, params, body }) => {
    const id = params['id'] ?? '';
    const items = csrOf(store, id).plan?.donationItems ?? [];
    const itemId = params['itemId'] ?? '';
    if (!items.some((item) => item.id === itemId)) throw new SandboxError(404, 'Donated item not found.');
    return { data: withDonationItems(store, id, items.map((item) => (item.id === itemId ? donationItemOf(itemId, body as DonationItemInput) : item))) };
  }),
  route('DELETE', '/events/:id/csr/donation-items/:itemId', ({ store, params }) => {
    const id = params['id'] ?? '';
    const items = csrOf(store, id).plan?.donationItems ?? [];
    return { data: withDonationItems(store, id, items.filter((item) => item.id !== params['itemId'])) };
  }),
  // The Northwind event's photos and letters: none in the sandbox.
  route('GET', `/events/${PORTAL_EVENT_ID}/attachments`, () => ({ data: [] })),
  route('GET', '/event-recaps', ({ store, query }) => {
    const event = query.get('event');
    const rows = allRecaps(store).filter((recap) => (event ? (typeof recap.event === 'string' ? recap.event : recap.event?._id) === event : true));
    return { data: rows, pagination: { page: 1, limit: 50, total: rows.length, totalPages: 1, hasNext: false, hasPrev: false } };
  }),
  route('GET', '/event-recaps/:id', ({ store, params }) => {
    const recap = allRecaps(store).find((row) => row._id === params['id']);
    if (!recap) throw new SandboxError(404, 'Recap not found.');
    return { data: recap };
  }),
  route('PUT', '/event-recaps/:id', ({ store, params, body }) => {
    const changes = body as { recap?: EventRecap['recap']; clientFeedback?: string | null };
    for (const event of store.portal.events) {
      const index = event.recaps.findIndex((row) => row._id === params['id']);
      const existing = event.recaps[index];
      if (!existing) continue;
      const updated: EventRecap = { ...existing, ...(changes.recap ? { recap: changes.recap } : {}), ...(changes.clientFeedback === undefined ? {} : { clientFeedback: changes.clientFeedback, clientFeedbackAt: now() }), updatedAt: now() };
      event.recaps[index] = updated;
      return { data: updated };
    }
    throw new SandboxError(404, 'Recap not found.');
  }),
  route('GET', '/events/:id/users', ({ store }) => ({ data: store.users })),
  route('GET', '/events/:id/family', ({ store }) => ({ data: store.family })),
  route('GET', '/events/:id/charity-candidates', ({ store }) => {
    const rank = { approved: 0, pending: 1, do_not_use: 2 } as const;
    const city = store.event.startVenue?.city ?? '';
    return { data: [...store.charities].sort((a, b) => rank[a.status] - rank[b.status] || Number(b.regions.some((region) => region.city === city)) - Number(a.regions.some((region) => region.city === city)) || a.name.localeCompare(b.name)) };
  }),
  route('PUT', '/events/:id/charity-plan', ({ store, body }) => {
    const plan = body as CharityPlan;
    store.event = { ...store.event, charityPlan: plan.charityId ? { ...plan, updatedAt: now() } : undefined } as EventDetail;
    return { data: store.event.charityPlan ?? null };
  }),
  route('POST', '/events/:id/contacts', ({ store, body }) => {
    const write = body as LocalContactWrite;
    const contact: LocalContact = { ...write, _id: newId(), createdAt: now() };
    store.event = { ...store.event, localContacts: [...(store.event.localContacts ?? []), contact] };
    return { data: store.event.localContacts };
  }),
  route('PATCH', '/events/:id/contacts/:contactId', ({ store, params, body }) => {
    const patch = body as Partial<LocalContactWrite>;
    store.event = { ...store.event, localContacts: (store.event.localContacts ?? []).map((contact) => (contact._id === params['contactId'] ? { ...contact, ...patch } : contact)) };
    return { data: store.event.localContacts };
  }),
  route('DELETE', '/events/:id/contacts/:contactId', ({ store, params }) => {
    store.event = { ...store.event, localContacts: (store.event.localContacts ?? []).filter((contact) => contact._id !== params['contactId']) };
    return { data: store.event.localContacts };
  }),
  route('POST', '/events/:id/recordings', ({ store, body }) => {
    const recording = recordingFor(body as Parameters<typeof recordingFor>[0]);
    store.event = { ...store.event, recordings: [...(store.event.recordings ?? []), recording] };
    return { data: store.event.recordings };
  }),
  route('DELETE', '/events/:id/recordings/:recordingId', ({ store, params }) => {
    store.event = { ...store.event, recordings: (store.event.recordings ?? []).filter((recording) => recording._id !== params['recordingId']) };
    return { data: store.event.recordings };
  }),
  route('GET', '/events/:id/shipping', ({ store }) => ({ data: store.shipping })),
  route('PUT', '/events/:id/shipping', ({ store, body }) => {
    const patch = body as Record<string, unknown>;
    const { shipTo, confirmed, ...profile } = patch;
    store.shipping = {
      ...store.shipping,
      profile: { ...store.shipping.profile, ...(profile as object), ...(shipTo === null ? { shipTo: undefined } : shipTo ? { shipTo } : {}), ...(confirmed === null ? { confirmed: undefined } : confirmed ? { confirmed: { ...(confirmed as object), at: now(), by: 'You' } } : {}) },
      confirmed: confirmed === null ? false : confirmed ? true : store.shipping.confirmed,
    };
    return { data: store.shipping };
  }),
  // Following and muting an event is a real preference; in the sandbox it just answers.
  route('PUT', '/notification-preferences/subjects/:type/:id', () => ({ data: { override: 'muted' } })),
  route('DELETE', '/notification-preferences/subjects/:type/:id', () => ({ data: { override: null } })),
  route('POST', '/users/lookup-by-emails', ({ store, body }) => {
    const emails = new Set(((body as { emails?: string[] }).emails ?? []).map((email) => email.toLowerCase()));
    return { data: store.users.filter((user) => user.email && emails.has(user.email.toLowerCase())) };
  }),
];
